/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Who manages an artifact — the `managedBy` axis** (`ARTIFACT_STORAGE_DESIGN_REVIEW.md` S12,
 * `OT2`/`OT5`, slice `3d`).
 *
 * Until `3d`, *system* was spelled `!ownerId`, so three different facts were one stored state:
 * *the platform manages this*, *transferred to nobody* and *its owner was deleted* (`#1035`). This
 * module is the one place that answers the question, and the console imports it rather than
 * mirroring it (`@artifact-stewardship`), so the badge and the permission check cannot disagree.
 *
 * **A ladder, not a set, and it stops short on purpose.** `system < owner < group`: `group`
 * arrives with `#980` as an addition to both lists below. Reach (`visibility`) is a separate axis
 * and nothing here reads it.
 *
 * Imports nothing, which is what lets it be aliased into the console.
 */

/**
 * The stewardship values this build understands, lowest first. **`bidbrain` is not a rung above the
 * others** (`RQ14`, slice 6): it says the content ships with bidBrain and is managed upstream, so nobody
 * in this deployment edits, deletes or transfers it. Only the seeder and import (`#948`) write it.
 */
export const ARTIFACT_STEWARDSHIPS = ['system', 'owner', 'bidbrain'] as const;

export type ArtifactStewardship = (typeof ARTIFACT_STEWARDSHIPS)[number];

/** The two fields stewardship is read from. Anything with an owner satisfies it. */
export interface HasStewardship {
  managedBy?: string | null;
  ownerId?: string | number | null;
}

/**
 * **Who manages this artifact?**
 *
 * A stated `managedBy` wins. **An absent one is read through `ownerId`**, and this is the only place
 * that reading is written. Absent is the ordinary state, not a legacy one: nothing stamps a value
 * on create or backfills one, because storage must not decide that an unowned row is
 * system-managed. *Unowned* is also what a deleted owner leaves behind (`#1035`,
 * `artifact.repository.spec.ts`). Only an act that decides stewardship writes it: a transfer
 * (`OT1`) today, and `#1035`'s orphan handling when it lands.
 *
 * **An unrecognised value answers `null`, never a guess.** A row written by a later build (`group`)
 * and read by this one must not be taken for `system`, because that would let `ad_ops` edit
 * something its manager scoped away from them. Callers treat `null` as *not system-managed*.
 */
export function resolveStewardship(artifact: HasStewardship | null | undefined): ArtifactStewardship | null {
  const stated = artifact?.managedBy;
  if (stated === null || stated === undefined || stated === '') {
    return artifact?.ownerId ? 'owner' : 'system';
  }
  return (ARTIFACT_STEWARDSHIPS as readonly string[]).includes(stated) ? (stated as ArtifactStewardship) : null;
}

/** Managed by the platform rather than by a user — what `ad_ops` may change, and what reads *System*. */
export function isSystemManaged(artifact: HasStewardship | null | undefined): boolean {
  return resolveStewardship(artifact) === 'system';
}

/**
 * **Shipped with bidBrain** (`RQ14`, ruled 2026-10-06): managed by bidBrain, so it is copied or extended
 * rather than edited, of any type. A **stored** fact, `managed_by = 'bidbrain'`, which no operator act
 * writes; only the seeder and import (`#948`) do.
 *
 * It replaced reading authorship (`createdBy: 'system'` on a template), which could not widen past
 * templates: the pre-`3a` normaliser wrote `'System'` for any ownerless create, and the local config DB
 * held eight non-template demand sources carrying it, none of them shipped (measured 2026-10-06). A
 * template promoted to system has no upstream and stays editable by whoever manages it.
 */
export function isShipped(artifact: HasStewardship | null | undefined): boolean {
  return resolveStewardship(artifact) === 'bidbrain';
}

/**
 * **The target of a transfer** (`OT2`).
 *
 * A steward, not a user id: `#980` adds `{ kind: 'group'; groupId }` as a third member, and
 * *promote to group* is that member's own use case. The referent rides with the member that
 * needs one, so `system` carries none.
 */
export type ArtifactSteward = { kind: 'user'; userId: string } | { kind: 'system' };

/** The two column values a steward writes. */
export function stewardshipColumns(steward: ArtifactSteward): {
  ownerId: string | null;
  managedBy: ArtifactStewardship;
} {
  switch (steward.kind) {
    case 'user':
      return { ownerId: steward.userId, managedBy: 'owner' };
    case 'system':
      return { ownerId: null, managedBy: 'system' };
  }
}

/** The steward an artifact has now, in the shape a transfer targets — `null` for a state no steward names. */
export function currentSteward(artifact: HasStewardship | null | undefined): ArtifactSteward | null {
  const stewardship = resolveStewardship(artifact);
  if (stewardship === 'system') return { kind: 'system' };
  if (stewardship === 'owner' && artifact?.ownerId) return { kind: 'user', userId: String(artifact.ownerId) };
  // `owner` with nobody named is an orphan (`#1035`): the state `3d` makes representable, and
  // exactly the one no transfer target can describe. Shipped content (`bidbrain`) is the other: it is
  // never a transfer's target, and `artifactAccess` refuses its transfer before this is read.
  return null;
}

/**
 * Read a steward from a request body. **`null` for anything malformed**, so the route refuses
 * rather than guessing — an empty `userId` read as *system* would be a promotion nobody asked for.
 */
export function parseSteward(value: unknown): ArtifactSteward | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as { kind?: unknown; userId?: unknown };
  if (candidate.kind === 'system') return { kind: 'system' };
  if (candidate.kind === 'user' && typeof candidate.userId === 'string' && candidate.userId.trim() !== '') {
    return { kind: 'user', userId: candidate.userId.trim() };
  }
  return null;
}

/** For a log line: `system`, or `user <id>`. Not operator-facing; the confirm names people. */
export function describeSteward(steward: ArtifactSteward): string {
  return steward.kind === 'system' ? 'system' : `user ${steward.userId}`;
}

/** Two stewards name the same manager. */
export function sameSteward(a: ArtifactSteward | null, b: ArtifactSteward | null): boolean {
  if (!a || !b) return false;
  if (a.kind !== b.kind) return false;
  return a.kind === 'system' || (b.kind === 'user' && a.userId === b.userId);
}
